import { useCallback, useEffect, useState } from 'react';
import { useAuth } from './auth-context';
import { getUserProfileCached } from './user-profile-cache';
import {
  normalizePlan,
  planHasTensionAI,
  PLAN_FEATURES,
  type SubscriptionPlan,
  type PlanFeatures,
} from './subscription-plans';

export interface SubscriptionState {
  plan: SubscriptionPlan;
  subscriptionStatus: string | null;
  loading: boolean;
  features: PlanFeatures;
  hasTensionAI: boolean;
  refresh: () => Promise<void>;
}

export function useSubscriptionPlan(): SubscriptionState {
  const auth = useAuth();
  const user = auth?.user ?? null;
  const accessToken = auth?.accessToken ?? null;

  const [plan, setPlan] = useState<SubscriptionPlan>('free');
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || !accessToken) {
      const local = typeof localStorage !== 'undefined'
        ? localStorage.getItem('tension_subscription')
        : null;
      setPlan(normalizePlan(local));
      setSubscriptionStatus(null);
      return;
    }
    setLoading(true);
    try {
      const data = await getUserProfileCached(accessToken);
      const p = normalizePlan(data.profile?.plan);
      setPlan(p);
      setSubscriptionStatus(data.profile?.subscriptionStatus ?? null);
      try {
        localStorage.setItem('tension_subscription', p);
      } catch {
        // ignore
      }
    } catch {
      const local = typeof localStorage !== 'undefined'
        ? localStorage.getItem('tension_subscription')
        : null;
      setPlan(normalizePlan(local));
    } finally {
      setLoading(false);
    }
  }, [user, accessToken]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const features = PLAN_FEATURES[plan];
  const hasTensionAI = planHasTensionAI(plan, subscriptionStatus);

  return {
    plan,
    subscriptionStatus,
    loading,
    features,
    hasTensionAI,
    refresh,
  };
}
